import { useEffect, useRef, useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./hooks/useAuth";
import ProtectedRoute from "./components/ProtectedRoute";
import Home from "./pages/Home";
import Login from "./pages/Login";
import Account from "./pages/Account";
import BrowseEvents from "./pages/BrowseEvents";
import CreateEvent from "./pages/CreateEvent";
import EventDashboard from "./pages/EventDashboard";
import DisciplineDetail from "./pages/DisciplineDetail";
import AdminBoutDetail from "./pages/AdminBoutDetail";
import RefBout from "./pages/RefBout";
import RefJoin from "./pages/RefJoin";
import RefPool from "./pages/RefPool";
import PublicEvent from "./pages/PublicEvent";
import PublicDiscipline from "./pages/PublicDiscipline";
import JudgeTools from "./pages/JudgeTools";
import JudgeJoin from "./pages/JudgeJoin";
import JudgeRegister from "./pages/JudgeRegister";
import FighterRegister from "./pages/FighterRegister";
import JudgeStandalone from "./pages/JudgeStandalone";
import JudgeScore from "./pages/JudgeScore";
import PoolJoin from "./pages/PoolJoin";
import JudgePool from "./pages/JudgePool";
import SpectateJoin from "./pages/SpectateJoin";
import SpectateView from "./pages/SpectateView";
import SpectatePool from "./pages/SpectatePool";

export default function App() {
  const location = useLocation();
  const [statusMessage, setStatusMessage] = useState("");
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const onStatus = (event) => setStatusMessage(event.detail || "");
    window.addEventListener("app-status", onStatus);
    return () => window.removeEventListener("app-status", onStatus);
  }, []);
  useEffect(() => {
    const updateConnection = () => {
      const connected = navigator.onLine;
      setOnline(connected);
      setStatusMessage(connected ? "Connection restored" : "Connection lost. Changes may not sync until reconnected");
    };
    window.addEventListener("online", updateConnection);
    window.addEventListener("offline", updateConnection);
    return () => {
      window.removeEventListener("online", updateConnection);
      window.removeEventListener("offline", updateConnection);
    };
  }, []);
  const mainRef = useRef(null);
  const previousPath = useRef(location.pathname);
  useEffect(() => {
    if (previousPath.current !== location.pathname) {
      previousPath.current = location.pathname;
      mainRef.current?.focus();
    }
  }, [location.pathname]);
  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    const updateTitle = () => {
      const heading = main.querySelector("h1");
      const nextTitle = `${heading?.textContent?.trim() || "Joy of HEMA"} | Joy of HEMA`;
      if (document.title !== nextTitle) document.title = nextTitle;
    };
    updateTitle();
    const observer = new MutationObserver(updateTitle);
    observer.observe(main, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, [location.pathname]);
  // DisciplineDetail (admin), EventDashboard (admin), and PublicDiscipline (spectator,
  // incl. the elimination bracket) all manage their own wider desktop layout — let them
  // break out of the mobile-width cap. Every other page stays phone-width even on desktop
  // by design.
  const isWideDesktopPage = /^\/event\/[^/]+\/discipline\/[^/]+$/.test(location.pathname)
    || /^\/event\/[^/]+\/discipline\/[^/]+\/bout\/[^/]+$/.test(location.pathname)
    || /^\/event\/[^/]+\/bout\/[^/]+$/.test(location.pathname)
    || /^\/event\/[^/]+$/.test(location.pathname)
    || /^\/public\/[^/]+\/discipline\/[^/]+$/.test(location.pathname)
    || location.pathname === "/account"
    || location.pathname === "/events";
  // Live spectator scoreboards (not the /spectate code-entry page) skip the support line.
  const isSpectatorLiveView = /^\/spectate\/.+/.test(location.pathname);
  const accessiblePageHeading = /^\/event\/[^/]+(\/discipline\/[^/]+)?\/bout\/[^/]+$/.test(location.pathname) ? "Bout results"
    : /^\/judge\/pool\//.test(location.pathname) ? "Judge pool bout"
    : /^\/judge\/[^/]+\/[^/]+$/.test(location.pathname) ? "Judge bout"
    : location.pathname === "/judge/standalone" ? "Standalone scoring"
    : /^\/ref\/[^/]+\/[^/]+$/.test(location.pathname) ? "Referee bout"
    : location.pathname === "/ref/join" ? "Join as referee"
    : /^\/spectate\/pool\//.test(location.pathname) ? "Spectate pool bout"
    : /^\/spectate\/[^/]+\/[^/]+$/.test(location.pathname) ? "Spectate bout"
    : null;

  return (
    <AuthProvider>
      <div className={`min-h-screen bg-dark-900 text-white font-body mx-auto max-w-lg ${isWideDesktopPage ? "lg:max-w-none" : ""}`}>
        <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[12000] focus:bg-white focus:text-black focus:p-3 focus:rounded-lg">Skip to content</a>
        <main id="main-content" ref={mainRef} tabIndex={-1}>
        <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">{statusMessage}</div>
        {!online && <div className="bg-yellow-800 text-white text-sm text-center p-2">Connection lost. Changes may not sync until reconnected.</div>}
        {accessiblePageHeading && <h1 className="sr-only">{accessiblePageHeading}</h1>}
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
          <Route path="/events" element={<BrowseEvents />} />
          <Route path="/create" element={<ProtectedRoute><CreateEvent /></ProtectedRoute>} />
          <Route path="/event/:eventId" element={<ProtectedRoute><EventDashboard /></ProtectedRoute>} />
          <Route path="/event/:eventId/discipline/:disciplineId" element={<ProtectedRoute><DisciplineDetail /></ProtectedRoute>} />
          <Route path="/event/:eventId/discipline/:disciplineId/bout/:boutId" element={<ProtectedRoute><AdminBoutDetail /></ProtectedRoute>} />
          <Route path="/event/:eventId/bout/:boutId" element={<ProtectedRoute><AdminBoutDetail /></ProtectedRoute>} />
          <Route path="/ref/join" element={<RefJoin />} />
          <Route path="/ref/:eventId/:boutId" element={<RefBout />} />
          <Route path="/ref/pool/:eventId/:disciplineId/:poolName" element={<RefPool />} />
          <Route path="/pool/:eventId/:disciplineId/:poolName" element={<PoolJoin />} />
          <Route path="/judge/pool/:eventId/:disciplineId/:poolName" element={<JudgePool />} />
          <Route path="/public/:eventId" element={<PublicEvent />} />
          <Route path="/public/:eventId/discipline/:disciplineId" element={<PublicDiscipline />} />
          <Route path="/judge" element={<JudgeTools />} />
          <Route path="/bout/join" element={<JudgeJoin />} />
          <Route path="/judge/join" element={<JudgeJoin />} />
          <Route path="/judge/register" element={<JudgeRegister />} />
          <Route path="/fighter/register" element={<FighterRegister />} />
          <Route path="/judge/standalone" element={<JudgeStandalone />} />
          <Route path="/judge/:eventId/:boutId" element={<JudgeScore />} />
          <Route path="/spectate" element={<SpectateJoin />} />
          <Route path="/spectate/pool/:eventId/:disciplineId/:poolName" element={<SpectatePool />} />
          <Route path="/spectate/:eventId/:boutId" element={<SpectateView />} />
        </Routes>
        </main>
        <footer className="text-center text-xs text-gray-600 py-4">
          {!isSpectatorLiveView && (
            <p className="mb-1">
              For support, please contact{" "}
              <a href="mailto:admin@joyofhema.com" className="text-gray-400 underline hover:text-gray-200">admin@joyofhema.com</a>
            </p>
          )}
          © 2026 JoyOfHEMA. All rights reserved.
        </footer>
      </div>
    </AuthProvider>
  );
}
